/**
 * CallSentimentIndicator — Indicador visual compacto del sentimiento del cliente.
 * Se puede usar inline en el active call card o en el dialer.
 */

import { useEffect, useState } from 'react';
import { InlineStack, Text, Tooltip } from '@/lib/polaris';
import { supabase } from '@/lib/supabase';

type Sentiment = 'positive' | 'neutral' | 'negative' | 'frustrated';

interface Props {
  crmCallId: string;
  /** Variant: 'badge' (compact) or 'pill' (with label) */
  variant?: 'badge' | 'pill';
}

const SENTIMENT: Record<Sentiment, { icon: string; color: string; bg: string; label: string; pulse: boolean }> = {
  positive: { icon: '😊', color: '#008060', bg: '#aee9d1', label: 'Positivo', pulse: false },
  neutral: { icon: '😐', color: '#6d7175', bg: '#e4e5e7', label: 'Neutral', pulse: false },
  negative: { icon: '😟', color: '#d72c0d', bg: '#fed3d1', label: 'Negativo', pulse: true },
  frustrated: { icon: '😤', color: '#b71c1c', bg: '#ffc4c4', label: 'Frustrado', pulse: true },
};

export function CallSentimentIndicator({ crmCallId, variant = 'badge' }: Props) {
  const [sentiment, setSentiment] = useState<Sentiment>('neutral');

  useEffect(() => {
    if (!crmCallId) return;

    // Initial load
    supabase
      .from('crm_calls')
      .select('live_sentiment')
      .eq('id', crmCallId)
      .single()
      .then(({ data }) => {
        if (data?.live_sentiment) setSentiment(data.live_sentiment as Sentiment);
      });

    // Realtime updates
    const channel = supabase
      .channel(`sentiment-${crmCallId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crm_calls',
          filter: `id=eq.${crmCallId}`,
        },
        (payload) => {
          const s = (payload.new as Record<string, unknown>).live_sentiment as Sentiment | null;
          if (s) setSentiment(s);
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [crmCallId]);

  const conf = SENTIMENT[sentiment] ?? SENTIMENT.neutral;

  if (variant === 'badge') {
    return (
      <Tooltip content={`Sentimiento: ${conf.label}`}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: conf.bg,
            fontSize: 16,
            cursor: 'default',
            animation: conf.pulse ? 'sf-sentiment-pulse 1.5s infinite' : undefined,
          }}
        >
          {conf.icon}
        </span>
      </Tooltip>
    );
  }

  // pill variant
  return (
    <Tooltip content={`Sentimiento del cliente: ${conf.label}`}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 10px',
          borderRadius: 12,
          background: conf.bg,
          color: conf.color,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'default',
          animation: conf.pulse ? 'sf-sentiment-pulse 1.5s infinite' : undefined,
        }}
      >
        {conf.icon} {conf.label}
      </span>
    </Tooltip>
  );
}

// CSS animation injected via style tag (avoid external CSS)
if (typeof document !== 'undefined' && !document.getElementById('sf-sentiment-styles')) {
  const style = document.createElement('style');
  style.id = 'sf-sentiment-styles';
  style.textContent = `
    @keyframes sf-sentiment-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }
  `;
  document.head.appendChild(style);
}

export default CallSentimentIndicator;
